import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import Clipboard from 'react-clipboard.js'
import classNames from 'classnames'
import Button from '@vtex/styleguide/lib/Button'
import Tag from '@vtex/styleguide/lib/Tag'
import {
  ToastProvider,
  ToastConsumer,
} from '@vtex/styleguide/lib/ToastProvider'
import every from 'lodash/every'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import {
  isWarningState,
  isCanceledState,
  isInvoicedState,
} from '@/checkout-instore-legacy/order/constants'
import {
  CREDIT_CARD_PAYMENT_SYSTEM,
  DEBIT_CARD_PAYMENT_SYSTEM,
} from '@/checkout-instore-legacy/constants/PaymentConstants'
import PackageSelector from '@/checkout-instore-legacy/package/components/PackageSelector'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import { getNativeAppInfo } from '@/checkout-instore-legacy/device/utils'
import { extractOrderPlacedHeaderInfo } from '@/checkout-instore-legacy/order/utils'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import { goToCustomLink } from '@/checkout-instore-legacy/utils/routesUtils'
import getClassStyle from '@/checkout-instore-legacy/utils/getClassStyle'
import { shouldPrintOrder } from '@/checkout-instore-legacy/utils/printUtils'
import { getOrderAsPackages } from '@/checkout-instore-legacy/shipping/utils'
import { DEFAULT_GROUP_PACKAGE_OPTIONS } from '@/checkout-instore-legacy/package/constants'
import ProductSummary from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductSummary'
import { getOriginSellerFromItem } from '@/checkout-instore-legacy/seller/utils'
import ProductName from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductName'
import {
  PackageInfoRow,
  PackageInfoSkeleton,
} from '../../../shipping/components/SlaSelector/PackageInfo'

import { CancelPartialOrderButton } from './CancelOrderButton'
import PackageHeader from './PackageHeader'
import PrintDetails from './PrintDetails'

const isOrderCanceled = (order) => {
  return isCanceledState(order.state) || isWarningState(order.state)
}

const isOrderInvoiced = (order) => isInvoicedState(order.state)

const OrderPlacedDetailActions = ({
  order,
  router,
  device,
  printImmediately,
}) => {
  const printDetailsVisible = shouldPrintOrder(order)

  const packages = getOrderAsPackages(order, DEFAULT_GROUP_PACKAGE_OPTIONS)

  const allowedPaymentsForPartialCancellation =
    getInstoreConfig().payments.partialCancellationEnabled || []
  const paymentsVerifiable = [
    CREDIT_CARD_PAYMENT_SYSTEM,
    DEBIT_CARD_PAYMENT_SYSTEM,
  ]

  const allowPartialCancellation = every(
    order.paymentData.transactions,
    (transaction) =>
      every(transaction.payments, (payment) => {
        if (paymentsVerifiable.includes(payment.paymentSystem)) {
          return allowedPaymentsForPartialCancellation.includes(
            payment.paymentSystem
          )
        }

        return true
      })
  )

  const isTakeAway = packages[0].selectedSlaType === 'take-away'
  const isLastOrdersPage = !!router.location.search // check if it's coming from Last Orders menu option
  const isCancelButtonVisible =
    allowPartialCancellation &&
    (!isOrderInvoiced(order) || isTakeAway) &&
    !isOrderCanceled(order) &&
    (getInstoreConfig().isCancelOrderAlwaysVisible || isLastOrdersPage)

  return (
    (printDetailsVisible || isCancelButtonVisible) && (
      <div className="no-print mt5 flex-l flex-row-l">
        {isCancelButtonVisible && (
          <div className="cancel-partial-order-container">
            <CancelPartialOrderButton orderId={order && order.orderId} />
          </div>
        )}
        {printDetailsVisible && (
          <div>
            <PrintDetails
              order={order}
              device={device}
              printImmediately={printImmediately}
              alreadyCalledHook={isLastOrdersPage}
            />
          </div>
        )}
      </div>
    )
  )
}

OrderPlacedDetailActions.propTypes = {
  order: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  device: PropTypes.shape({
    deviceId: PropTypes.string,
    deviceName: PropTypes.string,
  }),
  printImmediately: PropTypes.bool,
}

class OrderPlacedDetail extends PureComponent {
  constructor(props) {
    super(props)
    this.headerExtraInfo = extractOrderPlacedHeaderInfo(this.props.order)
    this.state = {
      orderPlacedInfo: this.headerExtraInfo || {
        state: null,
        invoiceNumber: null,
        invoiceUrl: null,
        trackingNumber: null,
      },
    }
  }

  componentDidMount() {
    WebViewBridge.sendEvent({ type: 'orderPlacedDetail' })
  }

  componentDidUpdate(prevProps) {
    if (this.props.order && !prevProps.order && !this.headerExtraInfo) {
      this.headerExtraInfo = extractOrderPlacedHeaderInfo(this.props.order)
      if (this.headerExtraInfo) {
        this.setOrderPlacedInfo(this.headerExtraInfo)
      }
    }
  }

  getStateTag = (state) => {
    if (isWarningState(state) || isCanceledState(state)) {
      return (
        <Tag type="error">
          <FormattedMessage
            id={`orderStatus.${this.state.orderPlacedInfo.state}`}
          />
        </Tag>
      )
    }

    return (
      <div className="c-on-base--inverted">
        <Tag
          bgColor={getClassStyle('bg-base--inverted', 'background-color')}
          color="currentColor"
        >
          <FormattedMessage
            id={`orderStatus.${this.state.orderPlacedInfo.state}`}
          />
        </Tag>
      </div>
    )
  }

  nativeAppInfo = getNativeAppInfo()

  isOrderCanceledOrInvoiced = () => {
    return (
      isCanceledState(this.props.order.state) ||
      isWarningState(this.props.order.state)
    )
  }

  setOrderPlacedInfo = (orderPlacedInfo) => {
    this.setState({ orderPlacedInfo })
  }

  handleLinkClick = (event) => {
    event.preventDefault()
    const link = {
      title: this.state.orderPlacedInfo.invoiceUrl,
      href: this.state.orderPlacedInfo.invoiceUrl,
      openExternal: true,
    }
    goToCustomLink(link, this.nativeAppInfo, this.props.router)
  }

  render() {
    const {
      order,
      router,
      showBorder,
      device,
      printImmediately,
      actionsDisabled,
    } = this.props

    const { sellers } = order

    const copyTextSuccess = this.props.intl.formatMessage({
      id: 'copyTextSucces',
    })

    return (
      <div
        data-testid="order-placed-detail"
        className={classNames(
          'order-placed-detail bg-base pv4 pv5-ns ph5 ph8-l relative mt4 br2-l',
          {
            'bb b--black-10': showBorder,
          }
        )}
      >
        <div
          id={`print-details-error-${order.orderId}`}
          className="no-print print-details-error"
        />
        <div className="pv3 pv4-ns no-print-spaces">
          <div className="flex flex-column flex-auto">
            <span className="f5 f4-ns mid-gray">
              <FormattedMessage id="orderReference" />
            </span>
            <div className="flex flex-column flex-row-l items-center-l">
              <span
                id="order-id"
                className="pv3 pv4-ns f3 f2-ns b black-90 tl-l"
              >
                {'#'}
                {order.orderId}
              </span>
              <span className="mh4-l">
                {this.state.orderPlacedInfo.state && (
                  <div>
                    {this.getStateTag(this.state.orderPlacedInfo.state)}
                  </div>
                )}
              </span>
            </div>
          </div>
          {actionsDisabled ? null : (
            <OrderPlacedDetailActions
              order={order}
              router={router}
              device={device}
              printImmediately={printImmediately}
            />
          )}
        </div>
        <div className="bb b--light-gray pb5 mb7">
          {this.state.orderPlacedInfo.invoiceNumber && (
            <div className="f6 f5-ns mb5 lh-copy">
              <div className="mid-gray mb0">
                <FormattedMessage id="invoiceNumber" />
              </div>
              <div className="black-80">
                {this.state.orderPlacedInfo.invoiceNumber}
              </div>
            </div>
          )}
          {this.state.orderPlacedInfo.invoiceUrl && (
            <div className="f6 f5-ns mb4 lh-copy">
              <div className="mid-gray">
                <FormattedMessage id="invoiceUrl" />
              </div>
              <div className="lh-copy c-action-primary hover-caction-primary db mb1">
                <a
                  href={this.state.orderPlacedInfo.invoiceUrl}
                  onClick={this.handleLinkClick}
                  className="truncate mw5 dib v-mid c-action-primary fw5 link mb0"
                >
                  {this.state.orderPlacedInfo.invoiceUrl}
                </a>
                <span className="no-print">
                  <ToastProvider positioning="window">
                    <ToastConsumer>
                      {({ showToast }) => (
                        <Clipboard
                          component="span"
                          data-clipboard-text={
                            this.state.orderPlacedInfo.invoiceUrl
                          }
                          onSuccess={() => showToast(copyTextSuccess)}
                        >
                          <Button
                            variation="tertiary"
                            size="small"
                            className="mb0"
                          >
                            <FormattedMessage id="copyButtonText" />
                          </Button>
                        </Clipboard>
                      )}
                    </ToastConsumer>
                  </ToastProvider>
                </span>
              </div>
            </div>
          )}
          {this.state.orderPlacedInfo.trackingNumber && (
            <div className="f6 f5-ns mb3 lh-copy">
              <div className="mid-gray mb0">
                <FormattedMessage id="trackingNumber" />
              </div>
              <div className="black-80">
                {this.state.orderPlacedInfo.trackingNumber}
              </div>
            </div>
          )}
        </div>
        <div>
          <PackageSelector
            header={<PackageHeader />}
            order={order}
            className=""
            headerClassName=""
            packageClassName="mt4"
            itemsClassName="b--light-gray"
            setOrderPlacedInfo={this.setOrderPlacedInfo}
            reversedOrder
          >
            {(item) => {
              const originSellerId = getOriginSellerFromItem(item)
              const originSeller = sellers?.find(
                (seller) => seller.id === originSellerId
              )
              return (
                <div key={item.id} className="pb2">
                  <ProductSummary item={item}>
                    <ProductSummary.Name>
                      <ProductName showSellerInfo={false} />
                    </ProductSummary.Name>
                    <ProductSummary.Origin>
                      <PackageInfoRow
                        labelId="shippingOriginSeller"
                        value={
                          originSeller ? (
                            originSeller.name
                          ) : (
                            <PackageInfoSkeleton />
                          )
                        }
                      />
                    </ProductSummary.Origin>
                    <ProductSummary.Content>
                      <ProductSummary.Price />
                    </ProductSummary.Content>
                  </ProductSummary>
                </div>
              )
            }}
          </PackageSelector>
        </div>
      </div>
    )
  }
}

OrderPlacedDetail.defaultProps = {
  printImmediately: true,
  showBorder: true,
}

OrderPlacedDetail.propTypes = {
  order: PropTypes.object.isRequired,
  printImmediately: PropTypes.bool,
  showBorder: PropTypes.bool,
  router: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  device: PropTypes.shape({
    deviceId: PropTypes.string,
    deviceName: PropTypes.string,
  }),
}

export default injectIntl(OrderPlacedDetail)
